// ***************************************************************************************
// Modes.
//
// A small typed configuration, not a plugin framework. Word selection and collapse are
// SHARED and live in the engine; a mode says what is legal, how it scores, when it ends
// and what it reports. Nothing here creates screens, and there are no disabled buttons
// for modes that do not exist yet.
//
// What this has to be able to express later, without the board being rewritten:
//
//   Word Ladder        - require length 3, then 4, then 5. That is `validateMove` seeing
//                        the move history, which it does.
//   Target word lists  - a fixed list to find, with order feedback. That is mode state
//                        plus an extra panel, which `state` and `panels` allow for.
//   Definitions        - a per-word lookup shown on demand. Additive; no rule changes.
//
// Only Daily Score Attack is built. The others are shapes this interface admits, not
// stubs waiting in the code.
// ***************************************************************************************

import type { Board } from './settle.ts';
import { DEFAULT_SCORING, WORD_LENGTH_MIN, type ScoringConfig } from './rules.ts';
import { LADDER_RUNGS } from './board.ts';
import { ACHIEVEMENTS_PER_DAY } from './achievements.ts';

/** A tile on the board. */
export interface TileRef {
  readonly x: number;
  readonly y: number;
}

/** One accepted word: the path taken, on the board as it was at the time. */
export interface Move {
  readonly tiles: readonly TileRef[];
  readonly word: string;
  /** What it was worth, INCLUDING any achievement bonus. */
  readonly score: number;
  readonly tier: number;
  /** Achievement ids this word took, if any. */
  readonly claimed: readonly string[];
}

export interface MoveRejection {
  readonly ok: false;
  readonly reason: string;
}

export type MoveVerdict = { readonly ok: true } | MoveRejection;

export interface CompletionVerdict {
  readonly finished: boolean;
  /** True only when every cell is empty. Not the same as "solved". */
  readonly cleared: boolean;
  readonly reason: 'cleared' | 'no-moves' | 'resigned' | null;
}

export interface ModeResultMetric {
  readonly id: string;
  readonly label: string;
  readonly value: string;
}

export interface ModeDefinition {
  readonly id: string;
  readonly title: string;
  /** The key into BOARD_MODES that decides the grid and the daily seed. */
  readonly boardMode: string;

  readonly scoring: ScoringConfig;

  /** The shortest word this mode accepts. Never below the engine's own minimum. */
  readonly minWordLength: number;

  /**
   * Whether the letters of a word have to touch each other on the board.
   *
   * True for every mode but Freedom. It is a mode rule rather than a constant because
   * the same engine scores both, and the same Worker verifies both - the alternative is
   * two code paths that disagree about what a legal move is.
   */
  readonly requiresAdjacency: boolean;

  /** Mode-specific extras. Score Attack has none, and says so rather than inventing them. */
  readonly hasShuffle: boolean;
  readonly hasHints: boolean;
  readonly hasTimer: boolean;
  readonly hasTargetList: boolean;

  /**
   * Whether a move is legal beyond the shared rules (on the board, adjacent, unused,
   * in the dictionary). Word Ladder's escalating length lives here.
   */
  validateMove(word: string, history: readonly Move[]): MoveVerdict;

  /**
   * Which word lengths could still be played, or null for "any".
   *
   * This is what the no-moves search asks. A mode that restricts lengths MUST answer it,
   * or the game will insist a board is playable when every remaining word is one the
   * mode itself would refuse.
   */
  playableLengths(history: readonly Move[]): number[] | null;

  /** What the finish screen reports, beyond the score. */
  metrics(state: ModeMetricInput): ModeResultMetric[];
}

export interface ModeMetricInput {
  readonly board: Board;
  readonly moves: readonly Move[];
  readonly score: number;
  readonly cleared: boolean;
  readonly tilesRemaining: number;
}

// ***************************************************************************************
// DAILY SCORE ATTACK - the first release.
//
// 5x5, no target list, no definitions, no timer, no shuffle. Players make their own words
// and the objective is the best total score. Free undo and restart, because the mode is an
// optimisation puzzle rather than a test of nerve.
// ***************************************************************************************
export const DAILY_SCORE_ATTACK: ModeDefinition = Object.freeze({
  id: 'daily-score-attack',
  title: 'Daily Score Attack',
  boardMode: 'puzzle',

  scoring: DEFAULT_SCORING,
  minWordLength: WORD_LENGTH_MIN,
  requiresAdjacency: true,

  hasShuffle: false,
  hasHints: false,
  hasTimer: false,
  hasTargetList: false,

  validateMove(): MoveVerdict {
    // Nothing beyond the shared rules. The shared rules are not weak - they already
    // require adjacency, no reuse, the minimum length and the dictionary.
    return { ok: true };
  },

  playableLengths(): number[] | null {
    return null;
  },

  metrics(input: ModeMetricInput): ModeResultMetric[] {
    let best: Move | null = null;
    let longest: Move | null = null;

    for (const move of input.moves) {
      if (!best || move.score > best.score) best = move;
      if (!longest || move.word.length > longest.word.length) longest = move;
    }

    return [
      {
        id: 'best-word',
        label: 'Best word',
        value: best ? `${best.word.toUpperCase()} (${best.score})` : '—',
      },
      {
        id: 'longest-word',
        label: 'Longest word',
        value: longest ? `${longest.word.toUpperCase()} (${longest.word.length})` : '—',
      },
      {
        id: 'words',
        label: 'Words found',
        value: String(input.moves.length),
      },
      {
        id: 'tiles',
        label: input.cleared ? 'Board' : 'Tiles left',
        value: input.cleared ? 'Cleared' : String(input.tilesRemaining),
      },
    ];
  },
});

// ***************************************************************************************
// LADDER MODE.
//
// One word of each length: 3, 4, 5, 6, 7. In ANY ORDER - the mode never dictates which
// rung to climb next, only that each is used once.
//
// The rungs total 25, which is exactly the 5x5 board, so a finished ladder is also a
// cleared board. That is not a coincidence, it is why those five lengths were chosen.
//
// The board is CONSTRUCTED to contain a complete ladder (see buildLadderBoard), so the
// promise "the grid always has them" is true of every published board. What it cannot
// promise is that every ORDER works: taking a word out collapses the grid under the
// others, so a route can strand. Undo is free, which is the answer to that.
// ***************************************************************************************
/** Which rungs are still to climb, in order. Empty when the ladder is finished. */
export function ladderRemaining(moves: readonly { word: string }[]): number[] {
  const done = new Set(moves.map((move) => move.word.length));

  return LADDER_RUNGS.filter((rung) => !done.has(rung));
}

export const LADDER: ModeDefinition = Object.freeze({
  id: 'ladder',
  title: 'Ladder',
  boardMode: 'ladder',

  scoring: DEFAULT_SCORING,
  minWordLength: WORD_LENGTH_MIN,
  requiresAdjacency: true,

  hasShuffle: false,
  hasHints: false,
  hasTimer: false,
  hasTargetList: false,

  validateMove(word: string, history: readonly { word: string }[]): MoveVerdict {
    const length = word.length;

    if (!LADDER_RUNGS.includes(length)) {
      const rungs = LADDER_RUNGS.join(', ');

      return { ok: false, reason: `ladder words are ${rungs} letters` };
    }

    if (history.some((move) => move.word.length === length)) {
      return { ok: false, reason: `${length} letters is already done` };
    }

    return { ok: true };
  },

  playableLengths(history: readonly { word: string }[]): number[] | null {
    return ladderRemaining(history);
  },

  metrics(input: ModeMetricInput): ModeResultMetric[] {
    const done = new Set(input.moves.map((move) => move.word.length));
    const missing = LADDER_RUNGS.filter((rung) => !done.has(rung));

    let best: Move | null = null;

    for (const move of input.moves) {
      if (!best || move.score > best.score) best = move;
    }

    return [
      {
        id: 'rungs',
        label: 'Ladder',
        value: `${done.size} of ${LADDER_RUNGS.length}`,
      },
      {
        id: 'missing',
        label: missing.length === 0 ? 'Finished' : 'Still needed',
        value: missing.length === 0 ? 'Every rung' : missing.map((n) => `${n}`).join(', '),
      },
      {
        id: 'best-word',
        label: 'Best word',
        value: best ? `${best.word.toUpperCase()} (${best.score})` : '—',
      },
      {
        id: 'tiles',
        label: input.cleared ? 'Board' : 'Tiles left',
        value: input.cleared ? 'Cleared' : String(input.tilesRemaining),
      },
    ];
  },
});

// ***************************************************************************************
// WORD SOUP HUNT.
//
// Play whatever words you like until nothing is left to play or you stop - the same open
// game as Score Attack. What is different is what is going on around it:
//
//   ACHIEVEMENTS   five standing challenges, drawn from ten kinds, different every day.
//                  Take one and that word scores DOUBLE and the challenge is struck out.
//   SHUFFLE        three per game. Rearranges the letters among the tiles still on the
//                  board, which is the way out of a grid with no words left in it.
//
// The board is constructed backwards from real words like every other built mode, so it
// can always be emptied - though emptying it is not the goal here, the score is.
// ***************************************************************************************
export const HUNT: ModeDefinition = Object.freeze({
  id: 'hunt',
  title: 'Word Soup Hunt',
  boardMode: 'hunt',

  scoring: DEFAULT_SCORING,
  minWordLength: WORD_LENGTH_MIN,
  requiresAdjacency: true,

  hasShuffle: true,
  hasHints: false,
  hasTimer: false,
  hasTargetList: false,

  validateMove(): MoveVerdict {
    // Nothing beyond the shared rules. The achievements are a reward, not a restriction:
    // a word that takes none is still a perfectly good word.
    return { ok: true };
  },

  playableLengths(): number[] | null {
    return null;
  },

  metrics(input: ModeMetricInput): ModeResultMetric[] {
    let best: Move | null = null;
    let longest: Move | null = null;
    let claimed = 0;

    for (const move of input.moves) {
      if (!best || move.score > best.score) best = move;
      if (!longest || move.word.length > longest.word.length) longest = move;

      claimed += move.claimed.length;
    }

    return [
      {
        id: 'achievements',
        label: 'Achievements',
        value: `${claimed} of ${ACHIEVEMENTS_PER_DAY}`,
      },
      {
        id: 'best-word',
        label: 'Best word',
        value: best ? `${best.word.toUpperCase()} (${best.score})` : '—',
      },
      {
        id: 'longest-word',
        label: 'Longest word',
        value: longest ? `${longest.word.toUpperCase()} (${longest.word.length})` : '—',
      },
      {
        id: 'words',
        label: 'Words found',
        value: String(input.moves.length),
      },
      {
        id: 'tiles',
        label: input.cleared ? 'Board' : 'Tiles left',
        value: input.cleared ? 'Cleared' : String(input.tilesRemaining),
      },
    ];
  },
});

// ***************************************************************************************
// WORD SOUP FREEDOM.
//
// The adjacency rule is gone. Take letters from anywhere on the board, in any order, and
// if they spell a word it is a word. They vanish and the grid collapses as it always
// does, which is the only thing left holding the game together.
//
// WHAT THAT CHANGES, and it is worth being honest about it: the board stops being a maze
// and becomes a bag of 25 letters. Nearly every board has something playable in it, so
// this mode is not really about finding a word - it is about which word, and about the
// collapse, since the order you empty the bag in is what decides the letters you are
// left holding at the end.
//
// So: no shuffle. Shuffling rearranges letters among the tiles, and when position does
// not constrain a word, a rearrangement changes nothing whatsoever. Offering the button
// would be offering a control that cannot help.
//
// The board is still CONSTRUCTED from real words, which means it can always be emptied -
// and here that promise is stronger than elsewhere, because the route does not have to
// be walked in any particular shape.
// ***************************************************************************************
export const FREEDOM: ModeDefinition = Object.freeze({
  id: 'freedom',
  title: 'Word Soup Freedom',
  boardMode: 'freedom',

  scoring: DEFAULT_SCORING,
  minWordLength: WORD_LENGTH_MIN,
  requiresAdjacency: false,

  hasShuffle: false,
  hasHints: false,
  hasTimer: false,
  hasTargetList: false,

  validateMove(): MoveVerdict {
    return { ok: true };
  },

  playableLengths(): number[] | null {
    return null;
  },

  metrics(input: ModeMetricInput): ModeResultMetric[] {
    let best: Move | null = null;
    let longest: Move | null = null;

    for (const move of input.moves) {
      if (!best || move.score > best.score) best = move;
      if (!longest || move.word.length > longest.word.length) longest = move;
    }

    return [
      {
        id: 'best-word',
        label: 'Best word',
        value: best ? `${best.word.toUpperCase()} (${best.score})` : '—',
      },
      {
        id: 'longest-word',
        label: 'Longest word',
        value: longest ? `${longest.word.toUpperCase()} (${longest.word.length})` : '—',
      },
      {
        id: 'words',
        label: 'Words found',
        value: String(input.moves.length),
      },
      {
        id: 'tiles',
        label: input.cleared ? 'Board' : 'Tiles left',
        value: input.cleared ? 'Cleared' : String(input.tilesRemaining),
      },
    ];
  },
});

export const MODES: Readonly<Record<string, ModeDefinition>> = Object.freeze({
  [DAILY_SCORE_ATTACK.id]: DAILY_SCORE_ATTACK,
  [LADDER.id]: LADDER,
  [HUNT.id]: HUNT,
  [FREEDOM.id]: FREEDOM,
});

export function modeById(id: string): ModeDefinition | null {
  return MODES[id] ?? null;
}
